import React, { createContext, useCallback, useMemo, useState } from "react";
import { CreateLearnerPayload } from "@iibf/shared-resources/dto/create-learner";
import { z } from "zod";
import _ from "lodash";

export const FORM_STAGE = {
  STARTED: "STARTED" as const,
  PERSONAL_DETAILS_CAPTURED: "PERSONAL_DETAILS_CAPTURED" as const,
  READY_FOR_SUBMISSION: "READY_FOR_SUBMISSION" as const,
  SUCCESSFUL: "SUCCESSFUL" as const,
  UNRESOLVED: "UNRESOLVED" as const,
};

export const STAGE: {
  [stageName: string]: {
    stepNumber: number;
    objective?: string;
  };
} = {
  [FORM_STAGE.STARTED]: {
    stepNumber: 1,
    objective: "Provide Learner Details",
  },
  [FORM_STAGE.PERSONAL_DETAILS_CAPTURED]: {
    stepNumber: 4,
    objective: "Verify Learner Details",
  },
  [FORM_STAGE.READY_FOR_SUBMISSION]: {
    stepNumber: 5,
  },
  [FORM_STAGE.UNRESOLVED]: {
    stepNumber: 6,
  },
  [FORM_STAGE.SUCCESSFUL]: {
    stepNumber: 7,
  },
};

export type FormStage = (typeof FORM_STAGE)[keyof typeof FORM_STAGE];

type RegistrationForm = Partial<
  z.infer<typeof CreateLearnerPayload> & {
    isPersonalDetailsFinalised: boolean;
    isPersonalDetailsVerified: boolean;
    personalDetailsErr: string;
    isFormSubmitted: boolean;
    isSuccessful: boolean;
    formSubmissionError: string;
    batchId: number;
    signupType: string;
  }
>;

export type RegistrationFormContextShape = {
  formStage: FormStage;
  formState: RegistrationForm;
  setFieldValue: (
    fieldName: keyof RegistrationForm,
    value: boolean | string | number | bigint | number[] | undefined
  ) => void;
  getFieldValue: (
    fieldName: keyof RegistrationForm
  ) => string | bigint | boolean | undefined | number | number[];
  clearForm: () => void;
};

const FormContext = createContext<RegistrationFormContextShape | {}>({});

export const FormContextProvider = ({ children }: FormContextProviderProps) => {
  const [formValues, setFormValues] = useState<RegistrationForm>(
    getFormValuesFromLocalStorage()
  );

  const formStage = useMemo(() => getFormStage(formValues), [formValues]);

  const setFieldValue: RegistrationFormContextShape["setFieldValue"] =
    useCallback(
      (fieldName, value) => {
        /*
         *
         *
         * Update value
         *
         *
         */
        Object.assign(formValues, { [fieldName]: value });

        getRectifiedFormState(formValues);

        /*
         *
         *
         * Set form state
         *
         *
         */
        localStorage.setItem("formState", JSON.stringify(formValues));
        setFormValues({ ...formValues });
      },
      [setFormValues, formValues]
    );

  const getFieldValue: RegistrationFormContextShape["getFieldValue"] =
    useCallback(
      (fieldName) => {
        return formValues[fieldName];
      },
      [formValues]
    );

  const clearForm: RegistrationFormContextShape["clearForm"] =
    useCallback(() => {
      for (const key in formValues) {
        delete formValues[key as keyof typeof formValues];
      }
      setFormValues({ ...formValues });
      localStorage.setItem("formState", "{}");
    }, [formValues]);

  const contextValue: RegistrationFormContextShape = useMemo(
    () => ({
      formStage,
      formState: formValues,
      setFieldValue,
      getFieldValue,
      clearForm,
    }),
    [formStage, formValues, setFieldValue, getFieldValue, clearForm]
  );

  return (
    <FormContext.Provider value={contextValue}>{children}</FormContext.Provider>
  );
};

const getFormValuesFromLocalStorage = (): RegistrationForm =>
  JSON.parse(localStorage.getItem("formState") || "{}");

type FormContextProviderProps = {
  children: any;
};

const getFormStage = (formState: RegistrationForm): FormStage => {
  let formStage: FormStage = FORM_STAGE.STARTED;

  if (
    formState.mobileNumber &&
    formState.name &&
    formState.gender &&
    formState.email &&
    formState.dateOfBirth &&
    formState.category &&
    formState.fatherName &&
    formState.isPhysicallyChallenged &&
    formState.highestQualification &&
    formState.addressLine1 &&
    formState.pincode &&
    formState.highestQualificationCertificateFileId &&
    formState.candidateSignatureFileId &&
    formState.isPersonalDetailsFinalised &&
    formState.partnerId
  )
    formStage = FORM_STAGE.PERSONAL_DETAILS_CAPTURED;
  if (formState.isPersonalDetailsVerified)
    formStage = FORM_STAGE.READY_FOR_SUBMISSION;
  if (formState.isFormSubmitted) {
    if (!formState.isSuccessful) formStage = FORM_STAGE.UNRESOLVED;
    else formStage = FORM_STAGE.SUCCESSFUL;
  }

  return formStage;
};

const getRectifiedFormState = (
  formState: RegistrationForm
): RegistrationForm => {
  // Reset personal verification status if user unfinalises data
  if (
    !formState.isPersonalDetailsFinalised &&
    formState.isPersonalDetailsVerified
  )
    formState.isPersonalDetailsVerified = false;

  // Unset form submission status on changing of personal details, so that form submission is triggered again
  if (!formState.isPersonalDetailsVerified && formState.isFormSubmitted) {
    formState.isFormSubmitted = false;
    formState.isSuccessful = false;
  }

  return formState;
};

export default FormContext;

declare global {
  interface Window {
    printFormState: () => void;
  }
}

window.printFormState = () => {
  const formState = JSON.parse(localStorage.getItem("formState") || "{}");
  const inputValuesFields: (keyof RegistrationForm)[] = [
    "name",
    "email",
    "mobileNumber",
    "gender",
  ];
  const inputValues = _.pick(formState, inputValuesFields);
  const formActivityState = _.omit(formState, inputValuesFields);
  console.log({
    inputValues,
    formActivityState,
  });
};
